import { createContext, useContext } from "react";

export const LanguageContext = createContext(null);
export const useLanguage = () => useContext(LanguageContext);

export const translations = {
  it: {
    nav: {
      apartment: "L'appartamento",
      gallery: "Galleria",
      location: "Posizione",
      calendar: "Disponibilità",
      reviews: "Recensioni",
      experiences: "Esperienze",
      book: "Prenota e paga",
    },
    hero: {
      eyebrow: "Isola delle Femmine · Palermo · Sicilia",
      lines: ["Il mare di Sicilia,", "la tua casa", "per l'estate."],
      sub: "Casa vacanze a due passi da spiagge e scogli: 2 camere da letto, soggiorno, cucina abitabile e due balconi. Fino a 6 posti letto, vicino a servizi e stazione.",
      ctaPrimary: "Prenota e paga",
      ctaSecondary: "Scopri la casa",
      stats: [
        { v: "6", l: "posti letto" },
        { v: "2", l: "balconi vista" },
        { v: "300 m", l: "dal mare" },
      ],
      scroll: "Scorri per esplorare",
    },
    marquee: [
      "Isola delle Femmine",
      "Palermo",
      "Sicilia",
      "Brezza di mare",
      "Fino a 6 ospiti",
      "Spiagge e scogli",
      "Stazione a due passi",
    ],
    chapters: [
      {
        num: "01",
        title: "Un rifugio sul mare",
        text: "A pochi passi dalle spiagge di sabbia e dalle scogliere di Isola delle Femmine, la casa accoglie chi cerca il ritmo lento della costa siciliana: colazioni al sole, bagni al mattino, tramonti dai balconi.",
        img: "https://images.unsplash.com/photo-1558436593-d7e0ffa49397?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
      },
      {
        num: "02",
        title: "Luce e spazi mediterranei",
        text: "Due camere da letto, un soggiorno luminoso con divano letto e una cucina abitabile dove ritrovarsi la sera. Ogni ambiente è pensato per famiglie e gruppi fino a sei ospiti.",
        img: "https://images.unsplash.com/photo-1688646953306-5ec93eab8c06?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
      },
      {
        num: "03",
        title: "L'isola che guarda la costa",
        text: "Di fronte al paese, l'isolotto con la sua torre è il simbolo di Isola delle Femmine e di una riserva naturale protetta. La stazione ferroviaria, a pochi minuti, collega la casa a Palermo in circa venti minuti.",
        img: "https://images.unsplash.com/photo-1558652361-3d3b72a5dc79?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
      },
    ],
    overview: {
      eyebrow: "L'appartamento",
      title: "Sei ambienti, un'unica atmosfera",
      text: "Una casa completa e funzionale, pronta ad accoglierti per un weekend o per tutta l'estate.",
      rooms: [
        { name: "Camera matrimoniale", desc: "Letto matrimoniale, armadio e luce naturale dal balcone.", img: "https://images.unsplash.com/photo-1704789202340-85a4786aa2c0?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400" },
        { name: "Camera doppia", desc: "Due letti singoli, ideale per bambini o amici.", img: "https://images.pexels.com/photos/26859049/pexels-photo-26859049.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
        { name: "Soggiorno", desc: "Divano letto per due ospiti, zona relax e pranzo.", img: "https://images.unsplash.com/photo-1688646953306-5ec93eab8c06?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400" },
        { name: "Cucina abitabile", desc: "Attrezzata per cucinare e mangiare insieme ogni giorno.", img: "https://images.unsplash.com/photo-1737233459465-8eaf6c7d8856?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400" },
        { name: "Bagno", desc: "Bagno completo con doccia e set cortesia.", img: "https://images.unsplash.com/photo-1756079664354-34944e001f6d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400" },
        { name: "Due balconi", desc: "Colazioni e aperitivi all'aperto con la brezza del mare.", img: "https://images.unsplash.com/photo-1763506425614-cdfee3349eff?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400" },
      ],
      capacity: [
        { v: "2", l: "camere da letto" },
        { v: "1", l: "bagno con doccia" },
        { v: "2", l: "balconi" },
        { v: "1+1", l: "soggiorno e cucina" },
      ],
    },
    gallery: {
      eyebrow: "Galleria",
      title: "Uno sguardo dentro e fuori casa",
      tabs: { all: "Tutto", interiors: "Interni", balconies: "Balconi", coast: "La costa" },
      note: "Foto dimostrative: verranno sostituite con le foto reali dell'appartamento.",
    },
    location: {
      eyebrow: "Posizione",
      title: "Tutto a pochi minuti a piedi",
      text: "Isola delle Femmine è un borgo marinaro a ovest di Palermo: la casa si trova in posizione comoda per vivere il mare senza rinunciare ai servizi.",
      points: [
        { title: "Mare, spiagge e scogli", desc: "Spiagge di sabbia e calette rocciose a circa 300 metri: si va a piedi, anche con i bambini." },
        { title: "Stazione ferroviaria", desc: "Il treno collega Isola delle Femmine a Palermo centrale in circa 20 minuti: niente auto, niente stress." },
        { title: "Servizi primari", desc: "Supermercati, bar, ristoranti di pesce, farmacia e market di paese sono tutti nelle immediate vicinanze." },
      ],
      mapTitle: "Isola delle Femmine (PA)",
    },
    guide: {
      eyebrow: "Guida ospiti",
      title: "La tua guida digitale",
      intro: "Tutto ciò che serve per un soggiorno sereno a Casa Isolana: le regole della casa, la mappa dei dintorni e i nostri consigli su mare, sapori e trasporti.",
      back: "Torna al sito",
      rules: {
        label: "Regolamento",
        title: "Le regole della casa",
        items: [
          "Massimo 6 ospiti: non è consentito ospitare persone non indicate nella prenotazione.",
          "Vietato fumare all'interno. Sui balconi sì, con posacenere e senza gettare nulla di sotto.",
          "Silenzio dalle 23:00 alle 8:00: le pareti sono sottili e i vicini gentili.",
          "Niente feste o eventi. Animali solo se concordati con il proprietario.",
          "Chiudete finestre e balconi quando uscite; spegnete luci e climatizzatori.",
          "Al check-out lasciate le chiavi sul tavolo della cucina e la porta chiusa.",
          "Segnalate subito guasti o rotture: risolviamo in fretta e senza drammi.",
        ],
      },
      practical: {
        label: "Info pratiche",
        title: "Come funziona la casa",
        items: [
          { title: "Check-in / Check-out", desc: "Arrivo 15:00–20:00, partenza entro le 10:00. Orari diversi su richiesta." },
          { title: "Cauzione e tassa", desc: "Cauzione di 150 € restituita al check-out salvo danni; tassa di soggiorno 1 € a persona per notte, in contanti all'arrivo." },
          { title: "Parcheggio", desc: "Parcheggio gratuito su strada nelle vicinanze; meglio arrivare prima delle 20:00 per trovare posto comodo." },
          { title: "Rifiuti", desc: "Raccolta differenziata: lunedì organico, martedì plastica, mercoledì carta, giovedì organico, venerdì vetro, sabato indifferenziato." },
        ],
      },
      area: {
        label: "Nei dintorni",
        title: "Mappa e consigli",
        mapTitle: "Isola delle Femmine (PA)",
        tips: [
          { title: "Spiagge e scogli", desc: "Spiaggia libera a circa 300 m (Via Palermo) e calette rocciose raggiungibili a piedi, anche con i bambini." },
          { title: "Dove mangiare", desc: "Trattoria Delfino per il pesce fresco; in centro trovi bar, gelaterie e pizzerie a pochi passi." },
          { title: "Spesa e servizi", desc: "Supermercato Conad in Via Roma, market di paese e farmacia a pochi minuti a piedi." },
          { title: "Muoversi", desc: "Stazione ferroviaria a 5 minuti a piedi: Palermo centrale in circa 20 minuti di treno, senza pensieri di parcheggio." },
          { title: "Da vedere", desc: "L'isolotto con la torre, simbolo del paese, e la riserva naturale orientata di Capo Gallo." },
        ],
      },
    },
    availability: {
      eyebrow: "Disponibilità",
      title: "Quando la casa è libera",
      text: "Il calendario è aggiornato in tempo reale: le date in terracotta sono occupate o bloccate dal proprietario. Le prenotazioni pagate bloccano le date automaticamente.",
      free: "Libero",
      busy: "Occupato",
      prev: "Mese precedente",
      next: "Mese successivo",
    },
    reviews: {
      eyebrow: "Recensioni",
      title: "Cosa dicono gli ospiti",
      text: "Testimonianze verificate di chi ha già scelto Casa Isolana per la propria vacanza in Sicilia.",
      empty: "Presto pubblicheremo le prime recensioni.",
      prev: "Precedente",
      next: "Successiva",
      submit: {
        title: "Lascia la tua recensione",
        intro: "Grazie per aver soggiornato a Casa Isolana. La tua opinione aiuta i prossimi ospiti.",
        alreadyDone: "Grazie! Abbiamo già ricevuto la tua recensione: sarà pubblicata dopo una rapida verifica.",
        rating: "Voto",
        titleField: "Titolo (facoltativo)",
        city: "Città (facoltativa)",
        text: "La tua esperienza",
        textPh: "Raccontaci il tuo soggiorno…",
        cta: "Invia recensione",
        sending: "Invio in corso…",
        success: "Grazie! La recensione sarà pubblicata dopo una rapida verifica.",
        error: "Qualcosa è andato storto. Riprova tra poco.",
        invalid: "Link non valido o scaduto.",
      },
    },
    experiences: {
      eyebrow: "Esperienze",
      title: "Vivi la Sicilia oltre la casa",
      text: "Consigli scelti a mano per completare il tuo soggiorno: mare, sapori e mani in pasta. Prenoti direttamente presso i fornitori.",
      cta: "Scopri e prenota",
      disclaimer: "I link portano a fornitori esterni indipendenti (GetYourGuide). Casa Isolana non ha rapporti commerciali con gli operatori e non è responsabile della prenotazione, dei prezzi o del servizio.",
      items: {
        boat: {
          title: "Giro in barca ai Faraglioni",
          lead: "Salpa dalla costa di Isola delle Femmine e nuota nelle calette di scogliera più belle della Sicilia occidentale.",
          duration: "Mezza giornata",
        },
        streetfood: {
          title: "Street food tour a Palermo",
          lead: "Ballarò, Capo, Vucciria: arancine, panelle e sfincione con una guida locale. Cena inclusa.",
          duration: "3 ore",
        },
        cooking: {
          title: "Cooking class siciliana",
          lead: "Impasta i cannoli e cuoci la pasta alla Norma con uno chef del posto. Ricette da portare a casa.",
          duration: "4 ore",
        },
      },
    },
    booking: {
      eyebrow: "Prenota",
      title: "Prenota il tuo soggiorno",
      text: "Scegli le date sul calendario e paga subito con carta, PayPal o bonifico. Conferma immediata.",
      name: "Nome e cognome",
      email: "Email",
      checkin: "Check-in",
      checkout: "Check-out",
      guests: "Ospiti",
      guestsSuffix: "ospiti",
      message: "Messaggio (facoltativo)",
      messagePh: "Raccontaci qualcosa sul tuo viaggio…",
      longStayHint: "Resta 7+ notti · 10% di sconto",
      submit: "Invia richiesta",
      sending: "Invio in corso…",
      success: "Richiesta inviata! Ti risponderemo presto.",
      error: "Qualcosa è andato storto. Riprova tra poco.",
      unavailable: "Le date selezionate non sono disponibili. Prova altre date o contattaci.",
      fillAll: "Compila nome, email e date per inviare.",
      phone: "Telefono",
      payTitle: "Come vuoi pagare?",
      methods: { card: "Carta di credito", paypal: "PayPal", bonifico: "Bonifico bancario" },
      payNow: "Prenota e paga",
      redirecting: "Ti portiamo al pagamento…",
      totalLabel: "Totale soggiorno",
      nightsLabel: "notti",
      selectDates: "Seleziona le date per vedere il prezzo",
      datesLabel: "Date del soggiorno",
      pickCheckin: "Tocca il giorno di arrivo. Le date in terracotta sono già occupate.",
      pickCheckout: "Ora tocca il giorno di partenza.",
      datesOk: "Date selezionate. Tocca un altro giorno per cambiarle.",
      resetDates: "Azzera le date",
      discountLabel: "Sconto lunga permanenza",
      taxNote: "Tassa di soggiorno 1 €/persona/notte, da pagare in loco.",
      depositNote: "Cauzione 150 €, restituita al check-out salvo danni.",
      bonificoTitle: "Prenotazione in attesa di bonifico",
      bonificoDesc: "Ti abbiamo inviato via email l'IBAN, l'importo e la causale. Le date restano riservate per 3 giorni: appena riceviamo il bonifico confermiamo la prenotazione.",
      holder: "Intestatario",
      amount: "Importo",
      causale: "Causale",
      codeLabel: "Codice sconto",
      codePlaceholder: "Hai un codice sconto? es. AMICI15",
      codeInvalid: "Codice non valido o scaduto",
      codeApplied: "Codice",
      shareBtn: "Condividi",
      shareGeneric: "Guarda questa casa vacanze a Isola delle Femmine, a 300 m dal mare: {url}",
      shareWithDates: "Che ne dite di Casa Isolana a Isola delle Femmine dal {from} al {to} ({nights} notti, € {total})? {url}",
      giftLabel: "Buono regalo",
      giftPlaceholder: "Hai un buono regalo? es. GIFT-XXXX-XXXX",
      giftInvalid: "Buono non valido o scaduto",
      giftApplied: "Buono regalo",
      giftFullCovered: "Prenotazione confermata! Il buono ha coperto l'intero soggiorno.",
      lastMinuteLabel: "Sconto ultimo minuto",
      catLabel: {
        low_season: "Base", high_season: "Alta stagione",
        weekend: "Weekend", period: "Periodo speciale", event: "Evento",
        manual: "Prezzo manuale", dynamic: "Dinamico",
      },
    },
    chat: {
      title: "Concierge Casa Isolana",
      subtitle: "Risponde in pochi secondi, in italiano o inglese",
      greeting: "Ciao! Sono il concierge di Casa Isolana. Chiedimi pure dell'appartamento, del mare o di come arrivare.",
      placeholder: "Scrivi una domanda…",
      open: "Apri la chat",
      error: "Il concierge non è disponibile ora. Riprova tra poco.",
    },
    lastMinute: {
      title: "Offerta ultimo minuto:",
      pill: "-{pct}% se parti entro il {date}",
      close: "Chiudi",
    },
    gift: {
      nav: "Buono regalo",
      eyebrow: "Buono regalo",
      title: "Regala un'estate a Isola delle Femmine",
      lead: "Una casa a due passi dal mare, la brezza di Sicilia, i tramonti dai balconi. Regala un soggiorno a chi ami: sceglie lui le date, tu ci pensi al pensiero.",
      perks: [
        "Valido 24 mesi, spendibile in una o più prenotazioni.",
        "Consegna via email subito o nel giorno che scegli tu.",
        "PDF elegante con QR code, pronto da stampare.",
      ],
      amountLabel: "Importo del buono",
      amountCustom: "Importo personalizzato (es. 250)",
      amountRange: "Da € 50 a € 2000.",
      buyerName: "Il tuo nome",
      buyerEmail: "La tua email",
      recipientName: "Nome del destinatario",
      recipientEmail: "Email del destinatario",
      messageLabel: "Messaggio personale (facoltativo)",
      messagePh: "Un pensiero per chi riceve il regalo…",
      deliveryLabel: "Data di consegna",
      deliveryHint: "Vuota = invia subito.",
      pdfLang: "Lingua del buono",
      payTitle: "Come vuoi pagare?",
      methods: { card: "Carta di credito", paypal: "PayPal" },
      total: "Totale",
      cta: "Regala ora",
      redirecting: "Ti portiamo al pagamento…",
      fillAll: "Compila nome ed email di acquirente e destinatario.",
      error: "Qualcosa è andato storto. Riprova tra poco.",
      terms: "L'importo è pagato al momento; il buono viene emesso e inviato (subito o alla data scelta) dopo la conferma del pagamento. Non è rimborsabile; l'eventuale residuo resta sul buono fino alla scadenza (24 mesi).",
    },
    footer: {
      title: "Casa Isolana",
      tagline: "Casa vacanze a Isola delle Femmine, Palermo",
      contacts: "Contatti",
      phoneLabel: "Telefono / WhatsApp",
      emailLabel: "Email",
      address: "Isola delle Femmine (PA), Sicilia",
      rights: "Tutti i diritti riservati.",
      whatsapp: "Scrivici su WhatsApp",
    },
    legal: {
      privacy: "Privacy Policy",
      cookie: "Cookie Policy",
      terms: "Termini e Condizioni",
      back: "Torna al sito",
      updated: "Ultimo aggiornamento",
      contact: "Per qualsiasi domanda su questo documento scrivi a",
      consent: "Inviando la richiesta dichiari di aver letto la {privacy} e di accettare i {terms}.",
    },
  },
  en: {
    nav: {
      apartment: "The apartment",
      gallery: "Gallery",
      location: "Location",
      calendar: "Availability",
      reviews: "Reviews",
      experiences: "Experiences",
      book: "Book & pay",
    },
    hero: {
      eyebrow: "Isola delle Femmine · Palermo · Sicily",
      lines: ["The Sicilian sea,", "your home", "for the summer."],
      sub: "A holiday apartment steps from sandy beaches and rocky coves: 2 bedrooms, living room, eat-in kitchen and two balconies. Sleeps up to 6, close to amenities and the train station.",
      ctaPrimary: "Book & pay",
      ctaSecondary: "Explore the home",
      stats: [
        { v: "6", l: "guests" },
        { v: "2", l: "balconies" },
        { v: "300 m", l: "to the sea" },
      ],
      scroll: "Scroll to explore",
    },
    marquee: [
      "Isola delle Femmine",
      "Palermo",
      "Sicily",
      "Sea breeze",
      "Sleeps up to 6",
      "Beaches & cliffs",
      "Station nearby",
    ],
    chapters: [
      {
        num: "01",
        title: "A haven by the sea",
        text: "A short walk from the sandy beaches and rocky shores of Isola delle Femmine, the apartment welcomes those seeking the slow rhythm of the Sicilian coast: sunny breakfasts, morning swims, sunsets from the balconies.",
        img: "https://images.unsplash.com/photo-1558436593-d7e0ffa49397?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
      },
      {
        num: "02",
        title: "Mediterranean light & space",
        text: "Two bedrooms, a bright living room with a sofa bed and an eat-in kitchen made for evening gatherings. Every room is designed for families and groups of up to six guests.",
        img: "https://images.unsplash.com/photo-1688646953306-5ec93eab8c06?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
      },
      {
        num: "03",
        title: "The islet watching the coast",
        text: "Facing the town, the islet with its tower is the symbol of Isola delle Femmine and of a protected nature reserve. The train station, minutes away, connects the apartment to Palermo in about twenty minutes.",
        img: "https://images.unsplash.com/photo-1558652361-3d3b72a5dc79?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
      },
    ],
    overview: {
      eyebrow: "The apartment",
      title: "Six spaces, one atmosphere",
      text: "A complete, functional home, ready to welcome you for a weekend or the whole summer.",
      rooms: [
        { name: "Master bedroom", desc: "Double bed, wardrobe and natural light from the balcony.", img: "https://images.unsplash.com/photo-1704789202340-85a4786aa2c0?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400" },
        { name: "Twin bedroom", desc: "Two single beds, perfect for kids or friends.", img: "https://images.pexels.com/photos/26859049/pexels-photo-26859049.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
        { name: "Living room", desc: "Sofa bed for two guests, lounge and dining area.", img: "https://images.unsplash.com/photo-1688646953306-5ec93eab8c06?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400" },
        { name: "Eat-in kitchen", desc: "Fully equipped for cooking and dining together daily.", img: "https://images.unsplash.com/photo-1737233459465-8eaf6c7d8856?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400" },
        { name: "Bathroom", desc: "Full bathroom with shower and courtesy set.", img: "https://images.unsplash.com/photo-1756079664354-34944e001f6d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400" },
        { name: "Two balconies", desc: "Open-air breakfasts and aperitifs in the sea breeze.", img: "https://images.unsplash.com/photo-1763506425614-cdfee3349eff?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400" },
      ],
      capacity: [
        { v: "2", l: "bedrooms" },
        { v: "1", l: "bathroom with shower" },
        { v: "2", l: "balconies" },
        { v: "1+1", l: "living room & kitchen" },
      ],
    },
    gallery: {
      eyebrow: "Gallery",
      title: "A look inside and outside",
      tabs: { all: "All", interiors: "Interiors", balconies: "Balconies", coast: "The coast" },
      note: "Placeholder photos: they will be replaced with real photos of the apartment.",
    },
    location: {
      eyebrow: "Location",
      title: "Everything within a short walk",
      text: "Isola delle Femmine is a seaside village west of Palermo: the apartment is perfectly placed to live by the sea without giving up everyday services.",
      points: [
        { title: "Sea, beaches & cliffs", desc: "Sandy beaches and rocky coves about 300 metres away: an easy walk, even with children." },
        { title: "Train station", desc: "Trains connect Isola delle Femmine to central Palermo in about 20 minutes: no car, no stress." },
        { title: "Everyday amenities", desc: "Supermarkets, cafés, seafood restaurants, pharmacy and village shops are all just around the corner." },
      ],
      mapTitle: "Isola delle Femmine (PA)",
    },
    guide: {
      eyebrow: "Guest guide",
      title: "Your digital guide",
      intro: "Everything you need for a relaxed stay at Casa Isolana: the house rules, a map of the area and our tips on the sea, food and getting around.",
      back: "Back to site",
      rules: {
        label: "House rules",
        title: "The house rules",
        items: [
          "Maximum 6 guests: hosting people not listed in the booking is not allowed.",
          "No smoking indoors. Balconies are fine, with an ashtray and nothing thrown over.",
          "Quiet hours from 23:00 to 8:00: thin walls, kind neighbours.",
          "No parties or events. Pets only by prior agreement with the host.",
          "Close windows and balcony doors when you go out; switch off lights and air conditioning.",
          "At check-out leave the keys on the kitchen table and close the door behind you.",
          "Report any fault or breakage right away: we fix things quickly and without fuss.",
        ],
      },
      practical: {
        label: "Practical info",
        title: "How the house works",
        items: [
          { title: "Check-in / Check-out", desc: "Arrival 15:00–20:00, departure by 10:00. Different times on request." },
          { title: "Deposit & tax", desc: "€150 security deposit returned at check-out unless damage is found; tourist tax €1 per person per night, cash on arrival." },
          { title: "Parking", desc: "Free street parking nearby; best to arrive before 20:00 to find a comfortable spot." },
          { title: "Waste", desc: "Separate collection: Mon organic, Tue plastic, Wed paper, Thu organic, Fri glass, Sat general waste." },
        ],
      },
      area: {
        label: "Around you",
        title: "Map & tips",
        mapTitle: "Isola delle Femmine (PA)",
        tips: [
          { title: "Beaches & cliffs", desc: "Free beach about 300 m away (Via Palermo) and rocky coves within walking distance, even with children." },
          { title: "Where to eat", desc: "Trattoria Delfino for fresh fish; in the centre you'll find cafés, ice-cream shops and pizzerias a few steps away." },
          { title: "Shopping & services", desc: "Conad supermarket on Via Roma, village shops and a pharmacy a few minutes on foot." },
          { title: "Getting around", desc: "Train station 5 minutes on foot: central Palermo in about 20 minutes by train, no parking worries." },
          { title: "Not to miss", desc: "The islet with its tower, symbol of the village, and the Capo Gallo nature reserve." },
        ],
      },
    },
    availability: {
      eyebrow: "Availability",
      title: "When the home is free",
      text: "The calendar is updated in real time: terracotta dates are booked or blocked by the owner. Paid bookings block their dates automatically.",
      free: "Free",
      busy: "Booked",
      prev: "Previous month",
      next: "Next month",
    },
    reviews: {
      eyebrow: "Reviews",
      title: "What guests say",
      text: "Verified testimonials from guests who chose Casa Isolana for their Sicilian holiday.",
      empty: "We will publish the first reviews soon.",
      prev: "Previous",
      next: "Next",
      submit: {
        title: "Leave your review",
        intro: "Thank you for staying at Casa Isolana. Your feedback helps future guests.",
        alreadyDone: "Thank you! We have already received your review: it will be published after a quick check.",
        rating: "Rating",
        titleField: "Title (optional)",
        city: "City (optional)",
        text: "Your experience",
        textPh: "Tell us about your stay…",
        cta: "Send review",
        sending: "Sending…",
        success: "Thank you! Your review will be published after a quick check.",
        error: "Something went wrong. Please try again shortly.",
        invalid: "Invalid or expired link.",
      },
    },
    experiences: {
      eyebrow: "Experiences",
      title: "Live Sicily beyond the house",
      text: "Hand-picked recommendations to round out your stay: sea, flavours and hands-on cooking. Book directly with the providers.",
      cta: "Discover & book",
      disclaimer: "Links go to independent third-party providers (GetYourGuide). Casa Isolana has no commercial ties with the operators and is not responsible for bookings, pricing or service.",
      items: {
        boat: {
          title: "Boat tour to the Faraglioni",
          lead: "Sail from the coast of Isola delle Femmine and swim in the finest coves of western Sicily.",
          duration: "Half day",
        },
        streetfood: {
          title: "Palermo street food tour",
          lead: "Ballarò, Capo, Vucciria: arancine, panelle and sfincione with a local guide. Dinner included.",
          duration: "3 hours",
        },
        cooking: {
          title: "Sicilian cooking class",
          lead: "Roll cannoli and cook pasta alla Norma with a local chef. Recipes to take home.",
          duration: "4 hours",
        },
      },
    },
    booking: {
      eyebrow: "Book",
      title: "Book your stay",
      text: "Pick your dates on the calendar and pay right away by card, PayPal or bank transfer. Instant confirmation.",
      name: "Full name",
      email: "Email",
      checkin: "Check-in",
      checkout: "Check-out",
      guests: "Guests",
      guestsSuffix: "guests",
      message: "Message (optional)",
      messagePh: "Tell us something about your trip…",
      longStayHint: "Stay 7+ nights · 10% off",
      submit: "Send request",
      sending: "Sending…",
      success: "Request sent! We will get back to you soon.",
      error: "Something went wrong. Please try again shortly.",
      unavailable: "The selected dates are not available. Try different dates or contact us.",
      fillAll: "Please fill in name, email and dates to send.",
      phone: "Phone",
      payTitle: "How would you like to pay?",
      methods: { card: "Credit card", paypal: "PayPal", bonifico: "Bank transfer" },
      payNow: "Book & pay",
      redirecting: "Taking you to payment…",
      totalLabel: "Total stay",
      nightsLabel: "nights",
      selectDates: "Select your dates to see the price",
      datesLabel: "Stay dates",
      pickCheckin: "Tap your arrival day. Terracotta dates are already taken.",
      pickCheckout: "Now tap your departure day.",
      datesOk: "Dates selected. Tap another day to change them.",
      resetDates: "Reset dates",
      discountLabel: "Long-stay discount",
      taxNote: "Tourist tax €1/person/night, paid on site.",
      depositNote: "€150 security deposit, returned at check-out unless damage is found.",
      bonificoTitle: "Booking awaiting bank transfer",
      bonificoDesc: "We've emailed you the IBAN, amount and reference. Your dates are held for 3 days: we confirm the booking as soon as we receive the transfer.",
      holder: "Account holder",
      amount: "Amount",
      causale: "Reference",
      codeLabel: "Discount code",
      codePlaceholder: "Have a discount code? e.g. AMICI15",
      codeInvalid: "Invalid or expired code",
      codeApplied: "Code",
      shareBtn: "Share",
      shareGeneric: "Check out this holiday home in Isola delle Femmine, Sicily — 300 m from the sea: {url}",
      shareWithDates: "How about Casa Isolana in Isola delle Femmine from {from} to {to} ({nights} nights, € {total})? {url}",
      giftLabel: "Gift card",
      giftPlaceholder: "Got a gift card? e.g. GIFT-XXXX-XXXX",
      giftInvalid: "Invalid or expired gift card",
      giftApplied: "Gift card",
      giftFullCovered: "Booking confirmed! The gift card covered your whole stay.",
      lastMinuteLabel: "Last-minute discount",
      catLabel: {
        low_season: "Base", high_season: "High season",
        weekend: "Weekend", period: "Special period", event: "Event",
        manual: "Manual price", dynamic: "Dynamic",
      },
    },
    chat: {
      title: "Casa Isolana Concierge",
      subtitle: "Replies in seconds, in Italian or English",
      greeting: "Hi! I'm the Casa Isolana concierge. Ask me anything about the apartment, the sea or how to get here.",
      placeholder: "Type a question…",
      open: "Open chat",
      error: "The concierge is unavailable right now. Please try again shortly.",
    },
    lastMinute: {
      title: "Last-minute offer:",
      pill: "-{pct}% if you leave by {date}",
      close: "Close",
    },
    gift: {
      nav: "Gift card",
      eyebrow: "Gift card",
      title: "Gift a summer in Isola delle Femmine",
      lead: "A home minutes from the sea, the Sicilian breeze, sunsets from the balconies. Gift a stay to someone you love: they pick the dates, you get the thoughtfulness credit.",
      perks: [
        "Valid 24 months, usable across one or more bookings.",
        "Emailed instantly or on a date of your choice.",
        "Elegant PDF with QR code, ready to print.",
      ],
      amountLabel: "Gift amount",
      amountCustom: "Custom amount (e.g. 250)",
      amountRange: "From € 50 to € 2000.",
      buyerName: "Your name",
      buyerEmail: "Your email",
      recipientName: "Recipient name",
      recipientEmail: "Recipient email",
      messageLabel: "Personal message (optional)",
      messagePh: "A thought for the recipient…",
      deliveryLabel: "Delivery date",
      deliveryHint: "Empty = send now.",
      pdfLang: "Card language",
      payTitle: "How would you like to pay?",
      methods: { card: "Credit card", paypal: "PayPal" },
      total: "Total",
      cta: "Send gift",
      redirecting: "Taking you to payment…",
      fillAll: "Please fill in buyer and recipient name/email.",
      error: "Something went wrong. Please try again shortly.",
      terms: "The amount is charged immediately; the gift card is issued and delivered (now or on the chosen date) once payment is confirmed. Non-refundable; residual balance stays on the card until expiry (24 months).",
    },
    footer: {
      title: "Casa Isolana",
      tagline: "Holiday apartment in Isola delle Femmine, Palermo",
      contacts: "Contacts",
      phoneLabel: "Phone / WhatsApp",
      emailLabel: "Email",
      address: "Isola delle Femmine (PA), Sicily",
      rights: "All rights reserved.",
      whatsapp: "Message us on WhatsApp",
    },
    legal: {
      privacy: "Privacy Policy",
      cookie: "Cookie Policy",
      terms: "Terms & Conditions",
      back: "Back to site",
      updated: "Last updated",
      contact: "For any question about this document write to",
      consent: "By sending the request you confirm you have read the {privacy} and accept the {terms}.",
    },
  },
};
